// Region-aware number/money formatting for every user-facing amount in the
// app — the numeric sibling of dateFormat.js.
//
// The active locale is `<ui language>-<organization country>` (e.g. `tr-TR`,
// `en-US`); money renders with the region's thousands separator and exactly
// two decimals (TR → 1.250.000,50, US → 1,250,000.50). Layout calls
// setNumberRegion() during render whenever the tenant config or UI language
// changes; until then formatters fall back to the browser locale.
//
// These helpers are for DISPLAY only — machine formats (API payloads, CSV
// exports) must keep using plain dot-decimal numbers.

let currentLocale; // BCP-47 tag, undefined = browser default
let currentLang;
let currentCountry;
const formatters = new Map();

/** Either argument may be omitted to keep the current value — see dateFormat. */
export function setNumberRegion(language, country) {
  if (language) currentLang = String(language).split('-')[0];
  if (country !== undefined) currentCountry = country;
  const lang = currentLang || 'en';
  const next = currentCountry ? `${lang}-${currentCountry}` : lang;
  if (next !== currentLocale) {
    currentLocale = next;
    formatters.clear();
  }
}

// Current resolved locale tag for components that build their own Intl
// formatters. Undefined = browser default.
export function getNumberLocale() {
  return currentLocale;
}

function getFormatter(opts) {
  const key = JSON.stringify(opts);
  let f = formatters.get(key);
  if (!f) {
    try {
      f = new Intl.NumberFormat(currentLocale, opts);
    } catch (_e) {
      f = new Intl.NumberFormat(undefined, opts);
    }
    formatters.set(key, f);
  }
  return f;
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

// Money: thousands grouping + exactly two decimals, in the region's
// separators. This is THE format for every amount/money field.
export function formatMoney(value, fallback = '—') {
  const n = toNumber(value);
  if (n === null) return fallback;
  return getFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

// Plain numbers (counts, quantities): grouping in the region's separators,
// decimals only when the value has them.
export function formatNumber(value, fallback = '—') {
  const n = toNumber(value);
  if (n === null) return fallback;
  return getFormatter({ maximumFractionDigits: 6 }).format(n);
}

// The region's separators — what a money input mask needs to accept and
// render while the user types.
export function getNumberSeparators() {
  const parts = getFormatter({ minimumFractionDigits: 1 }).formatToParts(1234.5);
  return {
    group: parts.find((p) => p.type === 'group')?.value || ',',
    decimal: parts.find((p) => p.type === 'decimal')?.value || '.',
  };
}

// Group an integer digit string ("1250000" → "1.250.000" in TR) without
// touching decimals — used by the live input mask.
export function groupIntDigits(digits) {
  if (!digits) return '';
  return getFormatter({ maximumFractionDigits: 0 }).format(Number(digits));
}

// Parse a region-formatted string back to a JS number (null when empty or
// unparseable). Group separators are dropped, the region's decimal separator
// becomes the dot.
export function parseLocaleNumber(str) {
  if (typeof str === 'number') return Number.isFinite(str) ? str : null;
  if (!str || typeof str !== 'string') return null;
  const { group, decimal } = getNumberSeparators();
  const cleaned = str
    .split(group).join('')
    .split(decimal).join('.')
    .replace(/[^\d.-]/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

// The display symbol for a currency code ("USD" → "$", "TRY" → "₺"), resolved
// through Intl so it follows the viewer's region conventions. Falls back to the
// code itself for anything Intl does not know — showing "AZN 1.000" is correct,
// where assuming a symbol would be wrong.
export function getCurrencySymbol(code) {
  const cur = String(code || '').trim().toUpperCase();
  if (!cur) return '';
  try {
    const parts = new Intl.NumberFormat(currentLocale, {
      style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value || cur;
  } catch (_e) {
    return cur;
  }
}
